import { normalizePhaseOrder } from './phaseTiming'

const INTERMISSION_NAME_PATTERN = /intermission/i
const PHASE_NAME_NUMBER_PATTERN = /(\d+(?:\.\d+)?)/
const NOTE_PHASE_EPSILON = 0.0001

type NamedPhase = {
  name: string
}

export const isIntermissionPhaseName = (name: string) =>
  INTERMISSION_NAME_PATTERN.test(name)

const parseNamedPhaseNumber = (name: string) => {
  const match = name.match(PHASE_NAME_NUMBER_PATTERN)
  if (!match) return null
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : null
}

const countMainPhasesThrough = (
  phases: NamedPhase[],
  lastDisplayIndex: number,
  order: number[],
) => {
  let mainPhaseCount = 0
  for (
    let displayIndex = 0;
    displayIndex <= lastDisplayIndex;
    displayIndex += 1
  ) {
    const phaseIndex = order[displayIndex] ?? displayIndex
    if (!isIntermissionPhaseName(phases[phaseIndex]?.name ?? '')) {
      mainPhaseCount += 1
    }
  }
  return mainPhaseCount
}

export const toNotePhaseNumber = (
  phases: NamedPhase[],
  phaseIndex: number,
  phaseOrder?: number[] | null,
) => {
  const phase = phases[phaseIndex]
  if (!phase) return phaseIndex + 1

  const order = normalizePhaseOrder(phases.length, phaseOrder)
  const displayIndex = order.indexOf(phaseIndex)
  const walkFrom = displayIndex >= 0 ? displayIndex : phaseIndex

  const namedNumber = parseNamedPhaseNumber(phase.name)
  if (isIntermissionPhaseName(phase.name)) {
    if (namedNumber !== null) return namedNumber + 0.5
    for (let cursor = walkFrom - 1; cursor >= 0; cursor -= 1) {
      const previousIndex = order[cursor]
      const previous =
        previousIndex === undefined ? undefined : phases[previousIndex]
      if (!previous || isIntermissionPhaseName(previous.name)) continue
      const previousNumber = parseNamedPhaseNumber(previous.name)
      if (previousNumber !== null) return previousNumber + 0.5
      return countMainPhasesThrough(phases, cursor, order) + 0.5
    }
    return 0.5
  }

  if (namedNumber !== null) return namedNumber
  return countMainPhasesThrough(phases, walkFrom, order)
}

export const formatNotePhaseNumber = (phaseNumber: number) => {
  if (Number.isInteger(phaseNumber)) return String(phaseNumber)
  return String(Math.round(phaseNumber * 10) / 10)
}

export const fromNotePhaseNumber = (
  phases: NamedPhase[],
  phaseRaw: number,
  phaseOrder?: number[] | null,
) => {
  const matchIndex = phases.findIndex((_, index) => {
    return (
      Math.abs(toNotePhaseNumber(phases, index, phaseOrder) - phaseRaw) <
      NOTE_PHASE_EPSILON
    )
  })
  if (matchIndex >= 0) return matchIndex

  if (!Number.isInteger(phaseRaw)) return null
  const sequentialIndex = phaseRaw - 1
  const order = normalizePhaseOrder(phases.length, phaseOrder)
  if (sequentialIndex < 0 || sequentialIndex >= order.length) return null
  return order[sequentialIndex] ?? null
}
